/**
 * Pure cart model for the SCHOOL_ADMIN bulk-ordering surface
 * (/orders/school). Lives in `lib/` (no React, no `@/api` runtime
 * imports) so the Zustand store + the unit tests can both pull it in
 * without dragging react-query into the test process.
 *
 * Conceptually mirrors the legacy `campus` app's `useSchoolOrderCartStore`
 * (add / update / remove categorisation, cost aggregation) but reshaped
 * for this app's `createCart → addCartItem → submitCart` REST pipeline:
 * the cart is a flat list of lines keyed by
 * `${date}::${mealtime}::${columnId}::${menuItemId}`, and checkout groups
 * them back into one OrderBatch per `(serviceDate, mealtime)` — the unit
 * the server's `submitCart` enforces.
 */

import type { Mealtime } from '@/api/school-orders'
import { compareMealtimes } from '@/lib/mealtime'
import { apportionQuantity } from '@/lib/school-orders/milk'

/**
 * One quantity entry in the cart. The `columnId` is the order column —
 * the age-group column (`LocationGradeRange.id`) or the group tuple key;
 * `gradeRangeId` is the portion tier (`GradeRange.id`) resolved from an
 * age-group column. Two columns can share a tier, so the line is keyed
 * by `columnId` to keep them distinct.
 *
 * `unitPriceCents` is the menu item's `customerPriceCents` captured at
 * add time so checkout can show a pre-submit subtotal without a priced
 * cart-read endpoint. Null when the menu item carries no price.
 */
export interface SchoolCartLine {
  /** `${date}::${mealtime}::${columnId}::${menuItemId}` */
  key: string
  locationId: string
  /** `YYYY-MM-DD` */
  date: string
  mealtime: Mealtime
  /** The order column — `LocationGradeRange.id` (age group) or the tuple key. */
  columnId: string
  /** Portion tier (`GradeRange.id`); denormalised for the submit payload. Null on group lines. */
  gradeRangeId: string | null
  /**
   * The `(groupDimension, groupValue)` column identity for non-AGE_GROUP
   * campuses; checkout sends it on the wire (age-group lines derive
   * their AGE_GROUP tuple from `columnId`). Optional for sessionStorage
   * back-compat.
   */
  groupDimension?: 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD' | null
  groupValue?: string | null
  menuItemId: string
  name: string
  imageUri: string | null
  unitPriceCents: number | null
  currency: string | null
  quantity: number
  /**
   * Pre-filled from the school's order percentages (by-component prefill); the
   * first user edit of the cell replaces the line without the flag, and
   * `addLines` never receives it (the panel strips it on add-to-cart).
   * Optional for sessionStorage back-compat.
   */
  suggested?: boolean
  /**
   * TYPED per-kind component quantities for a decomposable bundle line
   * (kind → count of that component to ship). **Sparse**: only kinds the
   * school actually typed appear, and an explicit `0` is a real value
   * ("ship none of this"), distinct from absent ("use the derived
   * default"). Absent kinds re-derive from `componentRateBps` as the meal
   * count changes; typed kinds hold.
   *
   * These are the numbers the school SEES, and checkout passes them
   * through unchanged — displayed is shipped, by construction. Optional
   * for sessionStorage back-compat.
   */
  componentQuantities?: Record<string, number> | null
  /**
   * The campus's delivery rate basis for this line's bundle (kind →
   * basis points, 0-10000), snapshotted at add-to-cart exactly like
   * `unitPriceCents` is. Two jobs: it lets the cart store and the
   * checkout page re-derive untouched kinds without access to the
   * settings query, and its KEY SET carries which kinds the bundle has
   * (they have no `parts` to consult). Optional for back-compat.
   */
  componentRateBps?: Record<string, number> | null
  /** Bundle portions per kind per meal, retained through checkout. */
  componentPartCounts?: Record<string, number>
  /**
   * PROVENANCE that travels to the server alongside the resolved
   * quantities: exactly which kinds the school TYPED. The wire map
   * (`overridesFromQuantities`) is COMPLETE by necessity — the shipment
   * materializer reads a missing kind as 0 — so without this list the
   * server cannot tell a typed number from a derived one, and a later
   * pre-deadline edit could never pick up a rate change.
   *
   * Absent = legacy line: the server stores NULL provenance and treats
   * every kind as typed. Optional for sessionStorage back-compat.
   */
  componentTypedKinds?: readonly string[] | null
  /**
   * Per-milk-SKU quantities (Component id → count) breaking down this
   * line's BEVERAGE total. Absent = let the server apportion from its own
   * share rows. Optional for sessionStorage back-compat.
   */
  milkSkuQuantities?: Record<string, number> | null
  /**
   * The line's menu item is a system milk row (`lineupMilkOptionCode`) —
   * the "milk is part of the meal" lines that DERIVE from the meal counts
   * (see `derived-milk.ts`). IDENTITY, not state: unlike `suggested` it
   * survives edits and the add-to-cart commit, so display counters can
   * keep excluding milk from "N meals" (Larkspur launch week: 20 meals +
   * 20 derived milks read as "40 meals selected"). Optional for persisted
   * back-compat — a line written before this field counts as a meal.
   */
  systemMilk?: boolean
}

/**
 * A cart line as persisted by an OLDER build — the percentage model.
 * Read only by `migrateCartLine`; never written.
 */
interface LegacySchoolCartLine extends SchoolCartLine {
  /** kind → integer percent 0-100. */
  componentSplitPct?: Record<string, number> | null
  /** Component id → integer percent of the BEVERAGE total. */
  milkSharePct?: Record<string, number> | null
}

/**
 * sessionStorage back-compat: convert a line persisted under the
 * PERCENTAGE model into the quantity model.
 *
 * Mandatory, not cosmetic. Both school stores persist to sessionStorage,
 * so a school mid-order when the deploy lands rehydrates lines written by
 * the old build. Dropping the old fields would silently discard their
 * component customization, and — worse — reusing a field name would read
 * `{VEGETABLE: 50}` as 50 UNITS instead of 50 PERCENT.
 *
 * Every legacy percentage becomes a TYPED quantity: the school had
 * committed to that split, and the percentages carry no rate basis to
 * re-derive from. `ceil` matches what the old build shipped after the
 * round-up fix, so a migrated line ships what its author last saw.
 */
export function migrateCartLine(line: LegacySchoolCartLine): SchoolCartLine {
  const { componentSplitPct, milkSharePct, ...rest } = line
  if (!componentSplitPct || Object.keys(componentSplitPct).length === 0) {
    return rest
  }
  const componentQuantities: Record<string, number> = {}
  for (const [kind, pct] of Object.entries(componentSplitPct)) {
    componentQuantities[kind] =
      kind === 'MAIN_ENTREE'
        ? Math.max(0, rest.quantity)
        : Math.max(0, Math.ceil((rest.quantity * Math.min(100, Math.max(0, pct))) / 100))
  }
  const milkQty = componentQuantities['BEVERAGE'] ?? 0
  const milkSkuQuantities =
    milkSharePct && Object.keys(milkSharePct).length > 0 && milkQty > 0
      ? apportionQuantity(milkQty, milkSharePct)
      : null
  return {
    ...rest,
    componentQuantities,
    ...(milkSkuQuantities ? { milkSkuQuantities } : {}),
  }
}

/**
 * Persisted-shape migrators for the two sessionStorage stores. Exported so
 * the conversion is unit-testable — the stores themselves only wire these
 * into zustand's `migrate` hook.
 */
export function migrateCartLines<T extends { linesByLocation?: Record<string, SchoolCartLine[]> }>(state: T): T {
  if (!state?.linesByLocation) {
    return state
  }
  return {
    ...state,
    linesByLocation: Object.fromEntries(
      Object.entries(state.linesByLocation).map(([locationId, lines]) => [
        locationId,
        (lines ?? []).map(migrateCartLine),
      ]),
    ),
  }
}

export function migrateDraftLines<T extends { draftsByLocation?: Record<string, Record<string, SchoolCartLine>> }>(
  state: T,
): T {
  if (!state?.draftsByLocation) {
    return state
  }
  return {
    ...state,
    draftsByLocation: Object.fromEntries(
      Object.entries(state.draftsByLocation).map(([locationId, byKey]) => [
        locationId,
        Object.fromEntries(Object.entries(byKey ?? {}).map(([key, line]) => [key, migrateCartLine(line)])),
      ]),
    ),
  }
}

/** `${date}::${mealtime}::${columnId}::${menuItemId}`. */
export function cartLineKey(date: string, mealtime: Mealtime, columnId: string, menuItemId: string): string {
  return `${date}::${mealtime}::${columnId}::${menuItemId}`
}

/** `${date}::${mealtime}` — the group key checkout submits one batch per. */
export function groupKey(date: string, mealtime: Mealtime): string {
  return `${date}::${mealtime}`
}

/**
 * Insert / update / remove a line in an immutable line list. A quantity
 * of `<= 0` removes the line (matching the legacy store's "remove when
 * zero" rule). Returns a new array; the input is never mutated.
 */
export function upsertLine(lines: SchoolCartLine[], line: SchoolCartLine): SchoolCartLine[] {
  const idx = lines.findIndex((l) => l.key === line.key)
  if (line.quantity <= 0) {
    if (idx === -1) {
      return lines
    }
    return [...lines.slice(0, idx), ...lines.slice(idx + 1)]
  }
  if (idx === -1) {
    return [...lines, line]
  }
  const next = [...lines]
  next[idx] = line
  return next
}

/** Total quantity across every line. */
export function totalQty(lines: SchoolCartLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity, 0)
}

/**
 * Quantity for DISPLAY counters ("N meals selected", the cart rail's item
 * count) — excludes system-milk lines, whose quantities track the meal
 * counts 1:1 at the default 100% beverage rate and would otherwise read
 * as a doubled order. Money helpers (`subtotalCents`) stay on ALL lines:
 * milk ships and pays as its own order line.
 */
export function mealQty(lines: SchoolCartLine[]): number {
  return totalQty(lines.filter((l) => !l.systemMilk))
}

/**
 * Pre-submit subtotal in cents — sum of `unitPriceCents * quantity` over
 * lines that carry a price. Returns `null` when NO line has a price (so
 * the caller can hide the dollar figure entirely rather than show $0.00).
 */
export function subtotalCents(lines: SchoolCartLine[]): number | null {
  let total = 0
  let priced = false
  for (const l of lines) {
    if (l.unitPriceCents != null) {
      total += l.unitPriceCents * l.quantity
      priced = true
    }
  }
  return priced ? total : null
}

/** A `(date, mealtime)` checkout group — one OrderBatch per group. */
export interface CheckoutGroup {
  date: string
  mealtime: Mealtime
  lines: SchoolCartLine[]
}

/**
 * Group cart lines into the `(date, mealtime)` batches `submitCart`
 * creates one of. Sorted by date then canonical mealtime order so the
 * checkout list + submit loop are deterministic.
 */
export function buildCheckoutPlan(lines: SchoolCartLine[]): CheckoutGroup[] {
  const groups = new Map<string, CheckoutGroup>()
  for (const line of lines) {
    if (line.quantity <= 0) {
      continue
    }
    const key = groupKey(line.date, line.mealtime)
    const group = groups.get(key)
    if (group) {
      group.lines.push(line)
    } else {
      groups.set(key, { date: line.date, mealtime: line.mealtime, lines: [line] })
    }
  }
  return [...groups.values()].sort((a, b) => a.date.localeCompare(b.date) || compareMealtimes(a.mealtime, b.mealtime))
}

/** A day's worth of checkout groups (its mealtimes, in canonical order). */
export interface CheckoutDay {
  date: string
  mealtimes: CheckoutGroup[]
}

/**
 * Roll the flat `(date, mealtime)` checkout groups up by day, so the
 * checkout page can render a day section heading over its mealtime rows.
 * Input is assumed pre-sorted (as `buildCheckoutPlan` returns it), so each
 * day's mealtimes keep canonical B→L→SN→SP order; days are sorted ascending.
 */
export function groupCheckoutByDay(groups: CheckoutGroup[]): CheckoutDay[] {
  const byDate = new Map<string, CheckoutGroup[]>()
  for (const group of groups) {
    const arr = byDate.get(group.date)
    if (arr) {
      arr.push(group)
    } else {
      byDate.set(group.date, [group])
    }
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, mealtimes]) => ({ date, mealtimes }))
}

/** A distinct menu item appearing in a cart-line list (matrix row metadata). */
export interface CartMatrixItem {
  menuItemId: string
  name: string
  imageUri: string | null
  unitPriceCents: number | null
  currency: string | null
}

/**
 * The distinct menu items in a line list, first-seen order preserved — the
 * rows of the editable (item × grade-column) matrix in the cart. Metadata is
 * captured from the first line of each item so a new cell (a grade column that
 * item wasn't ordered for) can be reconstructed into a cart line on confirm.
 */
export function cartMatrixItems(lines: SchoolCartLine[]): CartMatrixItem[] {
  const seen = new Map<string, CartMatrixItem>()
  for (const l of lines) {
    if (!seen.has(l.menuItemId)) {
      seen.set(l.menuItemId, {
        menuItemId: l.menuItemId,
        name: l.name,
        imageUri: l.imageUri,
        unitPriceCents: l.unitPriceCents,
        currency: l.currency,
      })
    }
  }
  return [...seen.values()]
}

/**
 * Whether a line's quantity is still a pure function of the meal counts —
 * i.e. the school has never touched it and a rate change may recompute it.
 *
 * The single definition of "derived", read by the checkout payload to stamp
 * `quantitySource: 'DERIVED'` on the wire. The server stores that, and
 * `already-ordered.ts` reads it back as `cellQtyIsDerived`, which is what
 * seeds `editDerivedKeys` and lets a pre-deadline edit re-derive the count.
 *
 * `systemMilk` alone is not enough: a school that types a milk count owns it
 * from then on, and re-deriving over that number is the bug in the other
 * direction.
 */
export function isDerivedQuantity(line: SchoolCartLine): boolean {
  return line.systemMilk === true && line.suggested === true
}

/**
 * The cart line to commit for one draft line.
 *
 * `suggested` is DRAFT state — a committed cart line is an order, not a
 * suggestion — so it is dropped here, as it always has been. **Except for
 * system milk**, where it is the only carrier of "this count still tracks the
 * meal counts".
 *
 * ## Why the exception exists (prod, Hillsborough, 2026-08-26)
 *
 * A placed Sep 1 order showed 45 meals against 3 + 2 = **5** milks. Nothing
 * was wrong with the derivation or the display: the order genuinely held 5,
 * frozen at whatever the meal counts were the moment the day was first
 * committed, and nothing was ever allowed to refresh it.
 *
 * The chain, every link verified:
 *   - the checkout payload stamps `quantitySource: 'DERIVED'` off
 *     `systemMilk && suggested` (`api/school-orders.ts`)
 *   - `suggested` was stripped from EVERY line on the way to the cart, so no
 *     school-placed milk line was ever stored `DERIVED`
 *   - `already-ordered.ts` reads that field for `cellQtyIsDerived`, so
 *     `beginEdit` seeded an EMPTY `editDerivedKeys`
 *   - the edit-mode re-derivation effect returns early on
 *     `editDerivedKeys.size === 0`, so raising meals 5 → 45 inside Edit left
 *     milk at 5 — and the save resubmitted 5.
 *
 * `handleSaveEdit` already re-stamps `suggested` from `editDerivedKeys` for
 * exactly this reason. It could never bootstrap, because the first placement
 * always went through the cart. This is that bootstrap.
 *
 * Meal lines still lose the flag: `prefill.ts` marks history-prefilled MEALS
 * `suggested` too, and those are a genuine human commitment once placed.
 */
export function committedCartLine(draftLine: SchoolCartLine): SchoolCartLine {
  const { suggested: _suggested, ...bare } = draftLine
  return isDerivedQuantity(draftLine) ? { ...bare, suggested: true } : bare
}

/**
 * True when two line lists represent the same order (by line key). Used to
 * decide the collapsed day-card "unsaved selections" dot: a day's draft
 * differs from what's committed in the cart.
 *
 * Compares the meal quantity AND the typed component quantities — under the
 * percentage model a component-only edit was scale-free and invisible here,
 * so changing fruit from 200 to 135 left the day looking committed.
 */
export function sameDayLines(a: readonly SchoolCartLine[], b: readonly SchoolCartLine[]): boolean {
  const sigOf = (lines: readonly SchoolCartLine[]) => {
    const map = new Map<string, string>()
    for (const l of lines) {
      if (l.quantity > 0) {
        const typed = l.componentQuantities ?? {}
        const parts = Object.keys(typed)
          .sort()
          .map((kind) => `${kind}=${typed[kind]}`)
        map.set(l.key, [String(l.quantity), ...parts].join('|'))
      }
    }
    return map
  }
  const am = sigOf(a)
  const bm = sigOf(b)
  if (am.size !== bm.size) {
    return false
  }
  for (const [k, v] of am) {
    if (bm.get(k) !== v) {
      return false
    }
  }
  return true
}
